import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { ExternalLinkIcon, GoogleTagManagerIcon, Typo3Icon, WeeblyIcon, WixIcon, WordPressIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { HorizontalScroll, Paragraph, ParagraphWithTooltip, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const TrackingInstallAsNativePlatform = () => {
  const translate = useTranslate();
  const supportCenterDomain = useSelector(getSupportCenterDomain);

  const nativePlatformList = useMemo(
    () => [
      {
        icon: <WordPressIcon />,
        subTitle: translate('trackingCode.installation.wordpress'),
        installationLink: translate('trackingCode.installation.wordpressUrl', { supportCenterDomain }),
        pluginPage: translate('trackingCode.plugin.wordpressUrl'),
      },
      {
        icon: <WixIcon />,
        subTitle: translate('trackingCode.installation.wix'),
        installationLink: translate('trackingCode.installation.wixUrl', { supportCenterDomain }),
        pluginPage: translate('trackingCode.plugin.wixUrl'),
      },
      {
        icon: <WeeblyIcon />,
        subTitle: translate('trackingCode.installation.weebly'),
        installationLink: translate('trackingCode.installation.weeblyUrl', { supportCenterDomain }),
        pluginPage: translate('trackingCode.plugin.weeblyUrl'),
      },
      {
        icon: <GoogleTagManagerIcon />,
        subTitle: translate('trackingCode.installation.googleTagManager'),
        installationLink: translate('trackingCode.installation.googleTagManagerUrl', { supportCenterDomain }),
        pluginPage: translate('trackingCode.plugin.googleTagManagerUrl'),
      },
      // commented based on VISA-9141
      // {
      //   icon: <DrupalIcon />,
      //   subTitle: translate('trackingCode.installation.drupal'),
      //   installationLink: translate('trackingCode.installation.drupalUrl', { supportCenterDomain }),
      //   pluginPage: translate('trackingCode.plugin.drupalUrl'),
      // },
      // {
      //   icon: <OctoberCMSIcon />,
      //   subTitle: translate('trackingCode.installation.octoberCMS'),
      //   installationLink: translate('trackingCode.installation.octoberCMSUrl', { supportCenterDomain }),
      //   pluginPage: translate('trackingCode.plugin.octoberCMSUrl'),
      // },
      {
        icon: <Typo3Icon />,
        subTitle: translate('trackingCode.installation.typo3'),
        installationLink: translate('trackingCode.installation.typo3'),
        pluginPage: translate('trackingCode.plugin.typo3'),
      },
    ],
    [translate, supportCenterDomain],
  );

  return (
    <div className='flex gap-3 flex-row flex-wrap'>
      {nativePlatformList.map((item, index) => (
        <NativePluginCard key={index} {...item} />
      ))}
    </div>
  );
};

type NativePluginCardType = {
  icon: JSX.Element;
  subTitle: string;
  installationLink: string;
  pluginPage: string;
};

const NativePluginCard = (props: NativePluginCardType) => {
  const { icon, subTitle, pluginPage } = props;

  const translate = useTranslate();

  return (
    <div
      className='bg-white-snow p-3 pr-7 mb-3 rounded-18 flex gap-3 items-center justify-between hover:bg-white-smoke cursor-pointer min-w-[4.5rem]'
      onClick={() => {
        window.open(pluginPage);
      }}
    >
      <div className='flex'>
        <div className='mr-3 h-12 min-w-12 rounded-24 bg-white flex items-center justify-center'>{icon}</div>
        <div className='flex justify-center flex-col overflow-hidden'>
          <Paragraph size={paragraphSizes.tiny} colorClassName='text-gray-devilSolid' weight={fontWeights.medium}>
            {translate('trackingCode.installation.installOn')}
          </Paragraph>
          <ParagraphWithTooltip colorClassName='text-gray-charcoal'>{subTitle}</ParagraphWithTooltip>
        </div>
      </div>
      <ExternalLinkIcon color='#C3C3C3' className='ml-auto' />
    </div>
  );
};

export default TrackingInstallAsNativePlatform;
